/**
 * Weight — spec page 04.
 *
 * The decision that shapes this page, straight from the spec:
 *
 *   > The headline number is the trend value, not the last reading, so a heavy
 *   > day does not read as failure.
 *
 * Body weight swings by a kilo on water alone. Showing the last reading as the
 * headline makes a good week look like a bad one at random, which is how people
 * stop trusting the number and then stop logging. The raw weigh-ins stay
 * visible as dots; the trend is what's promoted.
 */

import {
  useIntakeCorrelation,
  useOverview,
  useRecordWeight,
  useWeightTrend,
} from "@calorio/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { useSession } from "../app/session";

const RANGES = [
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "All", days: 730 },
];

export function Weight() {
  const ctx = useCalorio();
  const { signOut } = useSession();
  const [days, setDays] = useState(90);
  const trend = useWeightTrend(ctx, days);
  const record = useRecordWeight(ctx);
  // The goal lives on the Overview payload; reusing it here means the dashed
  // line and the "Day 47 of 90" header can never disagree.
  const overview = useOverview(ctx, 30);
  const correlation = useIntakeCorrelation(ctx, 12);
  const goal = overview.data?.goal ?? null;
  const [value, setValue] = useState("");

  if (trend.isLoading) return <p className="muted">Loading…</p>;
  if (!trend.data) return <p className="error">Couldn't load your weight history.</p>;

  const { entries, moving_average, change_kg } = trend.data;
  // The trend value, not the last reading — see the note above.
  const headline = moving_average.at(-1)?.[1] ?? entries.at(-1)?.weight_kg ?? null;
  const last = entries.at(-1);

  return (
    <div className="stack">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
        <h1 style={{ fontSize: "var(--step-2)", margin: 0 }}>Weight</h1>
        <p className="stat__label" style={{ margin: 0 }}>
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
          {entries[0] && ` since ${formatShort(entries[0].local_date)}`}
        </p>
        </div>
        {/* The bottom bar has no room for a fifth item, so on a phone this is
            where signing out lives — the same place the phone app puts it. */}
        <button className="phone-only signin__secondary" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      <div className="card">
        <div>
          <span className="stat__value num">{headline?.toFixed(1) ?? "—"}</span>
          <span className="stat__unit">kg</span>
          {change_kg !== null && (
            <span
              className={`stat__unit num ${change_kg <= 0 ? "delta--down" : "delta--up"}`}
              style={{ marginLeft: "var(--sp-3)" }}
            >
              {change_kg > 0 ? "+" : ""}
              {change_kg.toFixed(1)} kg
            </span>
          )}
        </div>
        <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
          trend value
          {last && ` · last entry ${formatShort(last.local_date)}, ${last.weight_kg.toFixed(1)} kg`}
        </p>

        <div style={{ display: "flex", gap: "var(--sp-2)", margin: "var(--sp-4) 0" }}>
          {RANGES.map((range) => (
            <button
              key={range.label}
              onClick={() => setDays(range.days)}
              className="num"
              style={{
                border: "1px solid var(--hairline)",
                background: days === range.days ? "var(--ink)" : "transparent",
                color: days === range.days ? "#fff" : "var(--ink-62)",
                borderRadius: "var(--r-pill)",
                padding: "4px 12px",
                fontSize: "var(--step--1)",
              }}
            >
              {range.label}
            </button>
          ))}
        </div>

        <TrendChart
          entries={entries}
          average={moving_average}
          goalKg={goal?.target_weight_kg ?? null}
        />
      </div>

      {goal && (
        <div className="grid grid--halves">
          <div className="card">
            <h2 className="card__title">Weekly rate</h2>
            <div>
              <span className="stat__value num">
                {goal.rate_kg_per_week?.toFixed(2) ?? "—"}
              </span>
              <span className="stat__unit">kg/wk</span>
            </div>
            {goal.target_rate_kg_per_week !== null && (
              <div className="stat__label num">
                target {goal.target_rate_kg_per_week.toFixed(2)} kg/wk
              </div>
            )}
          </div>

          <div className="card">
            <h2 className="card__title">To goal</h2>
            <div>
              <span className="stat__value num">{goal.remaining_kg?.toFixed(1) ?? "—"}</span>
              <span className="stat__unit">kg</span>
            </div>
            <div className="stat__label">
              {/* No projection when the rate is flat or pointing the wrong way.
                  A fabricated date is worse than an absent one. */}
              {goal.projected_date
                ? `projected ${new Date(`${goal.projected_date}T12:00:00Z`).toLocaleDateString(
                    undefined,
                    { day: "numeric", month: "short" },
                  )}`
                : "not moving toward the goal yet"}
            </div>
          </div>
        </div>
      )}

      {/* Goals live on "You", with the rest of the facts about the user; this
          is the doorway from the numbers they are measured against. */}
      <Link to="/you#goals" className="muted" style={{ fontSize: "var(--step--1)" }}>
        {goal ? "Adjust goal →" : "Set a weight goal →"}
      </Link>

      {/* The spec's "Against intake" panel. Descriptive, not causal — and it
          declines to draw itself on too little data rather than presenting
          noise as a finding. */}
      {correlation.data &&
        correlation.data.weeks_under + correlation.data.weeks_over >= 4 && (
          <div className="card">
            <h2 className="card__title">Against intake</h2>
            <p style={{ margin: 0 }}>
              Weeks averaging under{" "}
              <span className="num">{correlation.data.split_kcal.toLocaleString()}</span> kcal
              changed{" "}
              <span className="num delta--down">
                {correlation.data.mean_change_under_kg?.toFixed(1) ?? "—"} kg
              </span>
              ; weeks over changed{" "}
              <span className="num">
                {correlation.data.mean_change_over_kg?.toFixed(1) ?? "—"} kg
              </span>
              .
            </p>
            <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
              <span className="num">{correlation.data.weeks_under}</span> weeks under,{" "}
              <span className="num">{correlation.data.weeks_over}</span> over
            </p>
          </div>
        )}

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          const kg = Number(value);
          if (!Number.isFinite(kg) || kg <= 0) return;
          // Clear on success only. Wiping the field regardless meant a rejected
          // write looked exactly like an accepted one — which is how a wrong
          // HTTP verb in useRecordWeight went unnoticed on both clients.
          record.mutate({ weight_kg: kg }, { onSuccess: () => setValue("") });
        }}
      >
        <h2 className="card__title">Log weight</h2>
        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <input
            className="num"
            inputMode="decimal"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="82.4"
            aria-label="Weight in kilograms"
            style={{
              flex: 1,
              // Without this the input keeps its intrinsic ~20-character width
              // and refuses to shrink, pushing Save off the side of a phone.
              minWidth: 0,
              border: "1px solid var(--hairline)",
              borderRadius: "var(--r-md)",
              padding: "var(--sp-3)",
              fontSize: "var(--step-2)",
            }}
          />
          <button
            type="submit"
            disabled={record.isPending}
            style={{
              border: "none",
              background: "var(--action)",
              color: "#fff",
              borderRadius: "var(--r-md)",
              padding: "0 var(--sp-5)",
              fontWeight: 600,
            }}
          >
            {record.isPending ? "Saving…" : "Save"}
          </button>
        </div>
        {record.isError && (
          <p className="error" style={{ margin: "var(--sp-2) 0 0" }}>
            Couldn't save that. {record.error?.message ?? "Try again."}
          </p>
        )}
      </form>

      <div className="card">
        <h2 className="card__title">Entries</h2>
        {[...entries].reverse().map((entry) => (
          <div className="entry" key={entry.id}>
            <div className="entry__body">
              <div className="entry__name">{formatShort(entry.local_date)}</div>
              {entry.note && <div className="entry__meta">{entry.note}</div>}
            </div>
            <div className="entry__kcal num">{entry.weight_kg.toFixed(1)}</div>
          </div>
        ))}
        {/* The act this list is waiting for is the form a few inches up the
            same screen, so it is pointed at in words. A link would have to
            navigate away from the page the reader is already on, which is the
            one empty state in the app with nowhere to send anybody. */}
        {!entries.length && (
          <p className="manual__note">
            No weigh-ins yet. Save one under Log weight, just above, and it lands here — newest
            first, each with the day you stood on the scale.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Grey dots for raw weigh-ins, a teal line for the trend — the spec's exact
 * pairing. Hand-drawn SVG rather than a charting library: it's one line and a
 * scatter, and a library would bring a second set of colour defaults to fight
 * with the token palette.
 */
function TrendChart({
  entries,
  average,
  goalKg,
}: {
  entries: { local_date: string; weight_kg: number }[];
  average: [string, number][];
  goalKg?: number | null;
}) {
  // Counting out loud, because "two weigh-ins draws a trend" said to somebody
  // who has already logged one is indistinguishable from the sentence they read
  // before they logged it — and the difference between none and one is the only
  // progress there is to report at this stage. The form is below the chart on
  // this screen, which is where the next one goes; there is no page to send
  // anybody to.
  if (entries.length < 2)
    return (
      <p className="manual__note">
        Two weigh-ins make a trend
        {entries.length === 0 ? "; there are none yet" : "; there is one so far"}. Log weight,
        further down this screen, is where the next one goes.
      </p>
    );

  const width = 600;
  const height = 160;
  const pad = 8;

  const times = entries.map((e) => Date.parse(e.local_date));
  const weights = entries.map((e) => e.weight_kg);
  const [t0, t1] = [Math.min(...times), Math.max(...times)];
  // Pad the vertical range so the line never touches the frame — a trend
  // pinned to the top edge reads as a ceiling that isn't there.
  // Include the goal in the vertical range, or the dashed line sits off-canvas
  // exactly when it matters most — at the start, when you're furthest from it.
  const bounds = goalKg != null ? [...weights, goalKg] : weights;
  const [w0, w1] = [Math.min(...bounds) - 0.5, Math.max(...bounds) + 0.5];

  const x = (iso: string) => pad + ((Date.parse(iso) - t0) / (t1 - t0 || 1)) * (width - pad * 2);
  const y = (kg: number) => pad + (1 - (kg - w0) / (w1 - w0 || 1)) * (height - pad * 2);

  const line = average.map(([date, kg], i) => `${i ? "L" : "M"}${x(date)},${y(kg)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img"
      aria-label="Weight over time, with smoothed trend">
      {entries.map((entry) => (
        <circle
          key={entry.local_date}
          cx={x(entry.local_date)}
          cy={y(entry.weight_kg)}
          r={2.5}
          fill="var(--ink-62)"
        />
      ))}
      <path d={line} fill="none" stroke="var(--weight)" strokeWidth={2.5} strokeLinejoin="round" />
      {goalKg != null && (
        <>
          <line
            x1={0}
            x2={width}
            y1={y(goalKg)}
            y2={y(goalKg)}
            stroke="var(--ink-62)"
            strokeWidth={1}
            strokeDasharray="5 5"
          />
          <text
            x={width - 4}
            y={y(goalKg) - 5}
            textAnchor="end"
            fontSize="10"
            fill="var(--ink-62)"
          >
            goal {goalKg} kg
          </text>
        </>
      )}
    </svg>
  );
}

function formatShort(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}
